import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const goldStandards = [
  { label: '单人金标', desc: '单专家信度约0.03—0.06', sub: 'AI一致性上限约0.10—0.22' },
  { label: '面板金标', desc: '本科9专家面板信度约0.33', sub: '9学生面板约0.74' },
  { label: '综合金标', desc: '专家+学生明确规则组织', sub: '更稳定的比较基准' },
];

export default function P34ExpertModel({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-12">
        <div ref={titleRef} className="text-center mb-5 md:mb-7">
          <span className="inline-block text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-3 transition-all duration-700">专家经验锚定</span>
          <h2 className="font-heading text-2xl md:text-4xl lg:text-[2.75rem] font-bold text-foreground-950 transition-all duration-1000 delay-100">
            专家经验如何进入<span className="text-primary-600">专用评价模型</span>
          </h2>
        </div>

        {/* 桑基图式流程 */}
        <div className="max-w-5xl mx-auto mb-6 transition-all duration-1000 delay-200">
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-3">
            <div className="px-4 py-3 rounded-2xl glass-liquid border-primary-200/30 text-center">
              <i className="ri-video-line text-lg text-primary-500 mb-1" />
              <p className="text-[11px] font-body font-bold text-foreground-800">课程视频</p>
              <p className="text-[10px] text-foreground-500">+ 专家经验</p>
            </div>
            <i className="ri-arrow-right-line text-foreground-300" />
            <div className="px-4 py-3 rounded-2xl glass-liquid-gold border-accent-200/30 text-center">
              <i className="ri-price-tag-3-line text-lg text-accent-500 mb-1" />
              <p className="text-[11px] font-body font-bold text-foreground-800">专家标注</p>
              <p className="text-[10px] text-foreground-500">时段+指标标签</p>
            </div>
            <i className="ri-arrow-right-line text-foreground-300" />
            <div className="px-4 py-3 rounded-2xl glass-liquid border-primary-200/30 text-center">
              <i className="ri-list-check-3 text-lg text-primary-500 mb-1" />
              <p className="text-[11px] font-body font-bold text-foreground-800">评价标准</p>
              <p className="text-[10px] text-foreground-500">统一评价表</p>
            </div>
            <i className="ri-arrow-right-line text-foreground-300" />
            <div className="px-4 py-3 rounded-2xl glass-liquid-gold border-accent-200/30 text-center">
              <i className="ri-brain-line text-lg text-accent-500 mb-1" />
              <p className="text-[11px] font-body font-bold text-foreground-800">专用模型</p>
              <p className="text-[10px] text-foreground-500">多模态+多智能体</p>
            </div>
            <i className="ri-arrow-right-line text-foreground-300" />
            <div className="px-4 py-3 rounded-2xl glass-strong border-primary-200/30 text-center">
              <i className="ri-file-chart-line text-lg text-primary-500 mb-1" />
              <p className="text-[11px] font-body font-bold text-foreground-800">输出</p>
              <p className="text-[10px] text-foreground-500">总分+维度分+分档+证据链</p>
            </div>
          </div>
        </div>

        {/* 三阶梯金标 */}
        <div className="max-w-4xl mx-auto mb-5 transition-all duration-1000 delay-500">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {goldStandards.map((gs, i) => (
              <div key={gs.label} className="p-4 rounded-2xl glass border-white/50 text-center hover:border-primary-200/40 transition-all duration-500">
                <span className="inline-block w-8 h-8 rounded-full glass-blue border-primary-200/40 text-xs font-mono font-bold text-primary-600 leading-8 mb-2">{i + 1}</span>
                <h3 className="font-heading text-sm font-bold text-foreground-800">{gs.label}</h3>
                <p className="text-[11px] text-foreground-600 font-body mt-1">{gs.desc}</p>
                <p className="text-[10px] text-foreground-400 font-body mt-0.5">{gs.sub}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 底部 */}
        <div className="max-w-3xl mx-auto text-center transition-all duration-1000 delay-900">
          <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl glass border-white/50">
            <i className="ri-information-line text-sm text-foreground-400" />
            <span className="text-xs font-body text-foreground-500">
              模型学习的是专家<strong className="text-primary-700">为什么作出判断</strong>，而非"这节课很好"这样的结论
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}